import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  Body1Strong,
  Card,
  Link as FluentLink,
} from '@fluentui/react-components';

import { TItem } from '../../types/items.types';
import { AgoDateLabel } from '@shared/components/dates';
import { SmartTableCell } from '@shared/components/tables';
import { UserLabel } from '@modules/users';
import useItemTableStyles from '../../hooks/useItemTableStyles.hook';
import { buildHref } from '@shared/utils';
import { getItemPathByItemType } from '@modules/documents';
import RoleBadge from './RoleBadge';

type TProps = {
  items: TItem[];
};

/**
 * Table component for displaying pending approvals where the user has an action to take.
 */
const PendingApprovalsTable = ({ items }: TProps) => {
  const classes = useItemTableStyles();

  if (items.length === 0) {
    return <p>No pending approvals.</p>;
  }

  return (
    <Card className={classes.card}>
      <Table className={classes.table}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '300px' }}>
              <Body1Strong>Name</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '110px' }}>
              <Body1Strong>Your role</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '100px' }}>
              <Body1Strong>Approval initiated</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '80px' }}>
              <Body1Strong>Version</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '150px' }}>
              <Body1Strong>Requested by</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody className={classes.tableBody}>
          {items.map((item) => {
            const approvalData = item.approvalData;
            if (!approvalData) return null;

            const itemPath = getItemPathByItemType(item.type);

            // Document name link: opens with version + approval dialog
            const itemHref = buildHref(`/${itemPath}/${item._id}`, {
              view: approvalData.versionId,
              approval: approvalData.approvalId,
            });

            // Version link: opens with version only (no dialog)
            const versionHref = buildHref(`/${itemPath}/${item._id}`, {
              view: approvalData.versionId,
            });

            return (
              <TableRow key={`${item._id}-${approvalData.approvalId}`}>
                {/* Icon + Document name with product name below */}
                <SmartTableCell
                  variant={{ kind: 'item', item: { ...item, recentData: undefined } }}
                  descriptionHtml={approvalData.location}
                  onLinkClick={() => window.open(itemHref, '_blank')}
                />

                {/* Role Badge */}
                <TableCell>
                  <RoleBadge phase={approvalData.phaseKey} />
                </TableCell>

                {/* Approval created date */}
                <TableCell>
                  <AgoDateLabel>{approvalData.dateOpened}</AgoDateLabel>
                </TableCell>

                {/* Version (as link) */}
                <TableCell>
                  <FluentLink href={versionHref} target="_blank" appearance="subtle">
                    {approvalData.versionId}
                  </FluentLink>
                </TableCell>

                {/* Requested by */}
                <TableCell>
                  <UserLabel id={approvalData.creator} avatarProps={{}} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default PendingApprovalsTable;
